/**
 * PayosLivePanel — EV-1218 Phase 3 (venue dashboard).
 *
 * The software soundbox: live payment-LANDED confirmations for staff.
 *  1. Joins the venue's Socket.IO room (same room as table-calling) and
 *     listens for 'payment:confirmed' — emitted by the backend the moment
 *     PayOS's webhook says money arrived in the venue's linked bank account.
 *  2. Audible "ting" (WebAudio — no asset, unlocked by the enable button).
 *  3. Waiter-presented QR: staff enters amount (+table) → a dynamic PayOS QR
 *     renders BIG → customer camera-scans it from this screen → the SAME
 *     screen flips PAID ✓ when the money lands. (Static-QR fallback with a
 *     notice when the venue has no PayOS account yet.)
 *  4. Live feed of this session's confirmed payments (newest first).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../config/region';
import { buildVietQrPayload, renderBrandedQr } from '../../utils/vietQrBuilder';
import { dtLocale } from '../../utils/dateLocale';

function getAuthToken() {
  try {
    return (
      localStorage.getItem('venueapp_token') ||
      localStorage.getItem('venue_app_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('hl_auth_token') ||
      ''
    );
  } catch { return ''; }
}

// Socket host = API base without the /api suffix (same convention as useTableCalling).
function getSocketUrl() {
  const base = getApiBaseUrl() || '';
  const host = base.replace(/\/api\/?$/, '');
  return host || 'https://core.honestlee.app';
}

const fmtVnd = (n) => `₫${Number(n || 0).toLocaleString(dtLocale())}`;
const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

export default function PayosLivePanel({ slug, primaryMethod }) {
  const { t } = useTranslation();
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem('hl_dash_sound') === '1'; } catch { return false; }
  });
  const audioCtxRef = useRef(null);

  // Live feed of confirmed payments (socket-fed, session-scoped)
  const [feed, setFeed] = useState([]);
  const [socketUp, setSocketUp] = useState(false);
  // EV-1221 — live "I want to PAY NOW" requests from customers' tables.
  const [payRequests, setPayRequests] = useState([]);

  // Waiter-presented bill
  const [amount, setAmount] = useState('');
  const [table, setTable] = useState('');
  const [creating, setCreating] = useState(false);
  const [bill, setBill] = useState(null);   // { order_ref, amount, table_id, payos|null, paid|null }
  const qrRef = useRef(null);
  const billRef = useRef(null);
  billRef.current = bill;

  const amountNum = useMemo(() => {
    const n = parseInt(String(amount).replace(/[^\d]/g, ''), 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [amount]);

  // ── EV-1240: self-serve live-confirmation (SePay) toggle ──
  // enabled === null while loading; true/false once known. Lets an owner/manager
  // turn live payment confirmation ON/OFF without any SQL.
  const [liveEnabled, setLiveEnabled] = useState(null);
  const [liveBusy, setLiveBusy] = useState(false);

  const loadLiveConfirmation = useCallback(async () => {
    if (!slug) return;
    try {
      const base = getApiBaseUrl();
      const token = getAuthToken();
      const res = await fetch(`${base}/venue-dashboard/${encodeURIComponent(slug)}/live-confirmation`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json().catch(() => null);
      if (json && json.success) setLiveEnabled(json.enabled === true);
    } catch {
      /* best-effort — leave state as-is */
    }
  }, [slug]);

  const setLiveConfirmation = useCallback(async (enabled) => {
    if (!slug || liveBusy) return;
    setLiveBusy(true);
    try {
      const base = getApiBaseUrl();
      const token = getAuthToken();
      const res = await fetch(`${base}/venue-dashboard/${encodeURIComponent(slug)}/live-confirmation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ enabled: !!enabled }),
      });
      const json = await res.json().catch(() => null);
      if (json && json.success) {
        setLiveEnabled(json.enabled === true);
      } else {
        window.alert(t('vd.payosCouldNotUpdateLiveConfirmation'));
      }
    } catch {
      window.alert(t('vd.payosCouldNotUpdateLiveConfirmation'));
    } finally {
      setLiveBusy(false);
    }
  }, [slug, liveBusy]);

  useEffect(() => { loadLiveConfirmation(); }, [loadLiveConfirmation]);

  // ── the "ting" (two rising sine beeps — the loa thông báo, software edition) ──
  const playTing = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      // cash-register "cha-CHING" — two detuned bell strikes (matches the APK's
      // payment_cash.wav; Patrick 2026-07-13: "a cash register, this is ridiculous")
      const strike = (at, gain, dec) => {
        [2093, 2637, 3136, 3951].forEach((f, i) => {
          const osc = ctx.createOscillator();
          const g = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.value = f;
          const a0 = gain * [1, 0.6, 0.45, 0.3][i];
          g.gain.setValueAtTime(0.0001, ctx.currentTime + at);
          g.gain.exponentialRampToValueAtTime(a0, ctx.currentTime + at + 0.015);
          g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + dec);
          osc.connect(g).connect(ctx.destination);
          osc.start(ctx.currentTime + at);
          osc.stop(ctx.currentTime + at + dec + 0.05);
        });
      };
      strike(0, 0.35, 0.5);
      strike(0.13, 0.4, 0.8);
    } catch { /* sound is best-effort */ }
  }, []);

  const enableSound = () => {
    playTing();
    setSoundOn(true);
    try { localStorage.setItem('hl_dash_sound', '1'); } catch { /* noop */ }
  };

  // ── Socket.IO: join the venue room, listen for landed payments ──
  useEffect(() => {
    if (!slug) return undefined;
    const socket = io(getSocketUrl(), {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    socket.on('connect', () => {
      setSocketUp(true);
      // Joining the venue room is what makes us receive 'payment:confirmed'.
      // 'staff-dashboard' (not 'staff') so waiter CALLS don't route here.
      socket.emit('table:checkin', { venueSlug: slug, tableId: 'staff-dashboard' });
    });
    socket.on('disconnect', () => setSocketUp(false));
    socket.on('payment:confirmed', (p) => {
      if (!p || !p.order_ref) return;
      setFeed((prev) => prev.some((x) => x.order_ref === p.order_ref)
        ? prev
        : [{ ...p, at: p.confirmed_at || new Date().toISOString() }, ...prev].slice(0, 20));
      // The bill currently on screen just got paid → flip it green.
      const b = billRef.current;
      if (b && b.order_ref === p.order_ref) {
        setBill({ ...b, paid: { amount_vnd: p.amount_vnd, confirmed_at: p.confirmed_at } });
      }
      playTing();
    });
    // EV-1221 — a table asked to pay: surface it + ting so the waiter reacts.
    socket.on('table:pay-request', (p) => {
      if (!p || !p.tableId) return;
      setPayRequests((prev) => [
        { tableId: String(p.tableId), at: Date.now() },
        ...prev.filter((x) => x.tableId !== String(p.tableId)),
      ].slice(0, 8));
      playTing();
    });
    return () => { try { socket.disconnect(); } catch { /* noop */ } };
  }, [slug, playTing]);

  // ── Waiter-presented bill: create order (+PayOS link) → render QR ──
  const createBill = async () => {
    if (!slug || amountNum <= 0 || creating) return;
    setCreating(true);
    try {
      const base = getApiBaseUrl();
      const token = getAuthToken();
      const res = await fetch(`${base}/payments/attempt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          slug,
          amount_vnd: amountNum,
          source: 'adhoc_bill',
          table_id: table.trim() || null,
          memo: table.trim() ? t('vd.payosTableMemo', { table: table.trim() }) : null,
        }),
      });
      const json = await res.json().catch(() => null);
      if (!json?.success || !json.order_ref) {
        window.alert(t('vd.payosCouldNotCreateBill'));
        return;
      }
      setBill({
        order_ref: String(json.order_ref),
        amount: amountNum,
        table_id: table.trim() || null,
        payos: json.payos || null,
        paid: null,
      });
    } catch {
      window.alert(t('vd.payosCouldNotCreateBill'));
    } finally {
      setCreating(false);
    }
  };

  // Render the QR onto the canvas whenever a bill is showing.
  useEffect(() => {
    if (!bill || bill.paid || !qrRef.current) return;
    const p = bill.payos;
    const bin = p ? p.bin : (primaryMethod?.bank_bin || '');
    const acct = p ? p.account_number : (primaryMethod?.account_number || '');
    if (!bin || !acct) return;
    let cancelled = false;
    (async () => {
      try {
        const payload = buildVietQrPayload({
          bankBin: bin,
          accountNumber: acct,
          amount: bill.amount,
          memo: p ? p.description : `Bill${bill.table_id ? ` Table ${bill.table_id}` : ''}`,
          holderName: (p ? p.account_name : primaryMethod?.account_holder_name) || undefined,
        });
        if (cancelled || !qrRef.current) return;
        await renderBrandedQr(payload, qrRef.current, {
          bankCode: primaryMethod?.bank_code || null,
          bankName: primaryMethod?.bank_name || null,
        });
      } catch (e) {
        console.error('[PayosLivePanel] QR render failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [bill, primaryMethod]);

  // Poll fallback (covers socket drops): 3s status check while a PayOS bill shows.
  useEffect(() => {
    if (!bill || bill.paid || !bill.payos) return undefined;
    const base = getApiBaseUrl();
    let stopped = false;
    const tick = async () => {
      try {
        const r = await fetch(`${base}/payments/attempt/${encodeURIComponent(bill.order_ref)}/status`);
        const j = await r.json().catch(() => null);
        if (stopped) return;
        if (j?.success && j.confirmed && j.confirmation_source === 'webhook') {
          setBill((b) => (b && b.order_ref === bill.order_ref ? { ...b, paid: { amount_vnd: j.amount_vnd, confirmed_at: j.confirmed_at } } : b));
          playTing();
        }
      } catch { /* best-effort */ }
    };
    const iv = setInterval(tick, 3000);
    return () => { stopped = true; clearInterval(iv); };
  }, [bill, playTing]);

  const card = { background: 'white', borderRadius: 16, padding: 16, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e5e7eb' };
  const inp = { width: '100%', boxSizing: 'border-box', padding: '11px 13px', fontSize: 15, fontWeight: 700, border: '1.5px solid #e5e7eb', borderRadius: 12, outline: 'none', background: '#f9fafb' };

  return (
    <div style={{ ...card, borderTop: '4px solid #16a34a' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 22 }}>🔔</span>
        <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('vd.payosLivePayments')}</div>
        <span style={{ fontSize: 10.5, fontWeight: 800, color: socketUp ? '#166534' : '#9ca3af', background: socketUp ? '#dcfce7' : '#f3f4f6', padding: '3px 8px', borderRadius: 999 }}>
          {socketUp ? `● ${t('vd.payosLiveUpper')}` : `○ ${t('vd.payosConnecting')}`}
        </span>
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, marginBottom: 12 }}>
        {t('vd.payosShowCustomerQrHint')}
      </div>

      {/* Sound enable / test */}
      <button onClick={enableSound} style={{ width: '100%', marginBottom: 12, padding: '10px 0', borderRadius: 12, border: `1.5px solid ${soundOn ? '#16a34a' : '#e5e7eb'}`, background: soundOn ? '#f0fdf4' : '#fff', color: soundOn ? '#166534' : '#374151', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
        {soundOn ? `🔔 ${t('vd.payosSoundOnTapToTest')}` : `🔕 ${t('vd.payosEnableSound')}`}
      </button>

      {/* ── EV-1221: pay-now requests from tables ── */}
      {payRequests.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {payRequests.map((r) => (
            <button key={r.tableId}
              onClick={() => { setTable(r.tableId); setPayRequests((prev) => prev.filter((x) => x.tableId !== r.tableId)); }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 12px', borderRadius: 999, border: '1.5px solid #f59e0b', background: '#fffbeb', color: '#92400e', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
              💳 {t('vd.payosTableWantsToPay', { table: r.tableId })}
            </button>
          ))}
        </div>
      )}

      {/* ── The bill on screen ── */}
      {bill ? (
        bill.paid ? (
          <div style={{ textAlign: 'center', padding: '18px 8px', background: '#16a34a', borderRadius: 16, color: '#fff', marginBottom: 12 }}>
            <div style={{ fontSize: 52 }}>✅</div>
            <div style={{ fontSize: 22, fontWeight: 900, marginTop: 4 }}>{t('vd.payosPaidUpper')}</div>
            <div style={{ fontSize: 26, fontWeight: 900, marginTop: 6 }}>{fmtVnd(bill.paid.amount_vnd ?? bill.amount)}</div>
            <div style={{ fontSize: 13, fontWeight: 700, marginTop: 6, opacity: 0.95 }}>
              {bill.table_id ? `${t('vd.payTableN', { id: bill.table_id })} · ` : ''}#{bill.order_ref} · {fmtTime(bill.paid.confirmed_at)}
            </div>
            <button onClick={() => { setBill(null); setAmount(''); setTable(''); }} style={{ marginTop: 14, padding: '11px 30px', borderRadius: 12, border: 'none', background: '#fff', color: '#166534', fontSize: 14, fontWeight: 900, cursor: 'pointer' }}>
              {t('vd.payosNewBill')}
            </button>
          </div>
        ) : (
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            <div style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 55%, #f0f9ff 100%)', borderRadius: 16, padding: 14, border: '1px solid #bbf7d0', display: 'flex', justifyContent: 'center' }}>
              <canvas ref={qrRef} style={{ width: '100%', maxWidth: 320, height: 'auto', borderRadius: 8 }} />
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#111827', marginTop: 10 }}>
              {fmtVnd(bill.amount)}{bill.table_id ? ` · ${t('vd.payTableN', { id: bill.table_id })}` : ''}
            </div>
            {bill.payos ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 8, fontSize: 12.5, fontWeight: 700, color: '#166534' }}>
                <style>{`@keyframes hlDashDot { 0%,100% { opacity: 0.3; } 50% { opacity: 1; } }`}</style>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a', animation: 'hlDashDot 1.2s ease-in-out infinite' }} />
                {t('vd.payosWaitingForBank')}
              </div>
            ) : (
              <div style={{ marginTop: 8, fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '8px 10px', lineHeight: 1.45 }}>
                ⚠ {t('vd.payosNoLiveConfirmationYet')}
              </div>
            )}
            <button onClick={() => setBill(null)} style={{ marginTop: 10, padding: '9px 22px', borderRadius: 12, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              {t('vd.cancel')}
            </button>
          </div>
        )
      ) : (
        /* ── New bill inputs ── */
        <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', marginBottom: 4 }}>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="numeric" placeholder={t('vd.payosAmountPlaceholder')} style={{ ...inp, flex: 2 }} />
          <input value={table} onChange={(e) => setTable(e.target.value)} placeholder={t('vd.payTableOptionalShort')} style={{ ...inp, flex: 1 }} />
          <button onClick={createBill} disabled={creating || amountNum <= 0} style={{ flex: '0 0 auto', padding: '0 18px', borderRadius: 12, border: 'none', background: amountNum > 0 ? '#16a34a' : '#d1d5db', color: '#fff', fontSize: 14, fontWeight: 900, cursor: amountNum > 0 ? 'pointer' : 'default' }}>
            {creating ? '…' : t('vd.payosShowQr')}
          </button>
        </div>
      )}

      {/* ── Session feed ── */}
      {feed.length > 0 && (
        <div style={{ marginTop: 12, borderTop: '1px dashed #e5e7eb', paddingTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            {t('vd.payosConfirmedThisSession')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {feed.map((p) => (
              <div key={p.order_ref} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, background: '#f0fdf4', border: '1px solid #dcfce7' }}>
                <span style={{ fontSize: 15 }}>💰</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 900, color: '#166534' }}>{fmtVnd(p.amount_vnd)}</span>
                  {p.table_id && <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}> · {t('vd.payTableN', { id: p.table_id })}</span>}
                </div>
                <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtTime(p.at)} · #{p.order_ref}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── EV-1240: self-serve live-confirmation settings row ── */}
      {liveEnabled === false && (
        <div style={{ marginTop: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#92400e', marginBottom: 2 }}>
            ⚠ {t('vd.payosLiveConfirmationOff')}
          </div>
          <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.45, marginBottom: 10 }}>
            {t('vd.payosLiveConfirmationOffHint')}
          </div>
          <button
            onClick={() => setLiveConfirmation(true)}
            disabled={liveBusy}
            style={{ width: '100%', padding: '10px 0', borderRadius: 10, border: 'none', background: liveBusy ? '#d1d5db' : '#16a34a', color: '#fff', fontSize: 13, fontWeight: 800, cursor: liveBusy ? 'default' : 'pointer' }}
          >
            {liveBusy ? '…' : t('vd.payosTurnOnLiveConfirmation')}
          </button>
        </div>
      )}
      {liveEnabled === true && (
        <div style={{ marginTop: 12, borderTop: '1px dashed #e5e7eb', paddingTop: 10, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#6b7280' }}>
          <span style={{ fontWeight: 800, color: '#166534' }}>● {t('vd.payosLiveConfirmationOn')}</span>
          <span
            onClick={() => {
              if (liveBusy) return;
              // eslint-disable-next-line no-alert
              if (window.confirm(t('vd.payosTurnOffConfirm'))) setLiveConfirmation(false);
            }}
            style={{ marginLeft: 'auto', textDecoration: 'underline', cursor: liveBusy ? 'default' : 'pointer', fontWeight: 700 }}
          >
            {t('vd.payosTurnOff')}
          </span>
        </div>
      )}
    </div>
  );
}
